<template>
  <div class="monitoramento-cobrancas-jobs">
    <div
      class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3"
    >
      <div>
        <h5 class="mb-1">
          Monitoramento operacional
        </h5>

        <small class="text-muted">
          Acompanhe filas e problemas do processamento automático de cobranças.
        </small>
      </div>

      <button
        type="button"
        class="btn btn-outline-secondary btn-sm"
        :disabled="loading"
        @click="carregarTudo"
      >
        <span
          v-if="loading"
          class="spinner-border spinner-border-sm me-1"
          role="status"
          aria-hidden="true"
        ></span>

        <i
          v-else
          class="bi bi-arrow-clockwise me-1"
        ></i>

        Atualizar
      </button>
    </div>

    <div
      v-if="errorMessage"
      class="alert alert-danger"
      role="alert"
    >
      {{ errorMessage }}
    </div>

    <div
      v-if="loadingResumo && !resumo"
      class="card shadow-sm mb-3"
    >
      <div class="card-body py-5 text-center">
        <div
          class="spinner-border spinner-border-sm text-primary"
          role="status"
        ></div>

        <div class="mt-2 text-muted">
          Carregando monitoramento...
        </div>
      </div>
    </div>

    <template v-else>
      <div class="monitoramento-resumo-grid mb-3">
        <div
          v-for="card in resumoCards"
          :key="card.key"
          class="card shadow-sm"
        >
          <div class="card-body py-3">
            <div class="small text-muted mb-1">
              {{ card.label }}
            </div>

            <div class="fs-4 fw-semibold">
              {{ card.value }}
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm">
        <div
          class="card-header bg-white d-flex flex-wrap justify-content-between align-items-center gap-2"
        >
          <div>
            <strong>Problemas operacionais</strong>

            <span
              v-if="paginacao.total > 0"
              class="badge text-bg-secondary ms-2"
            >
              {{ paginacao.total }}
            </span>
          </div>

          <small class="text-muted">
            Somente leitura
          </small>
        </div>

        <div class="card-body p-0">
          <div
            v-if="loadingProblemas"
            class="py-5 text-center"
          >
            <div
              class="spinner-border spinner-border-sm text-primary"
              role="status"
            ></div>

            <div class="mt-2 text-muted">
              Carregando problemas...
            </div>
          </div>

          <div
            v-else-if="problemas.length === 0"
            class="py-5 text-center text-muted"
          >
            <i
              class="bi bi-check-circle fs-3 d-block mb-2"
            ></i>

            Nenhum problema operacional encontrado.
          </div>

          <div
            v-else
            class="table-responsive"
          >
            <table class="table table-hover align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>Situação</th>
                  <th>Tipo</th>
                  <th>Referência</th>
                  <th class="text-center">Tentativas</th>
                  <th>Diagnóstico</th>
                  <th>Disponível em</th>
                  <th>Prazo do processamento</th>
                  <th>Finalizado em</th>
                  <th>Atualizado em</th>
                </tr>
              </thead>

              <tbody>
                <tr
                  v-for="item in problemas"
                  :key="item.id"
                >
                  <td>
                    <span
                      class="badge"
                      :class="categoriaBadgeClass(item.categoria)"
                    >
                      {{ categoriaLabel(item.categoria) }}
                    </span>
                  </td>

                  <td>
                    {{ tipoLabel(item.tipo) }}
                  </td>

                  <td class="text-nowrap">
                    {{ referenciaLabel(item.referencia) }}
                  </td>

                  <td class="text-center">
                    {{ item.tentativas }}
                  </td>

                  <td>
                    <code v-if="item.ultimoErroCodigo">
                      {{ item.ultimoErroCodigo }}
                    </code>

                    <span
                      v-else
                      class="text-muted"
                    >
                      —
                    </span>
                  </td>

                  <td class="text-nowrap">
                    {{ formatTimestamp(item.disponivelEm) }}
                  </td>

                  <td class="text-nowrap">
                    {{ formatTimestamp(item.leaseExpiraEm) }}
                  </td>

                  <td class="text-nowrap">
                    {{ formatTimestamp(item.finalizadoEm) }}
                  </td>

                  <td class="text-nowrap">
                    {{ formatTimestamp(item.atualizadoEm) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div class="d-flex flex-wrap justify-content-between align-items-center gap-3 mt-3">
        <div>Mostrando {{ startRow }} a {{ endRow }} de {{ paginacao.total }} problemas</div>
        <div class="d-flex align-items-center gap-2">
          <BFormSelect
            v-model="limitePorPagina"
            :options="pageOptions"
            :disabled="loading"
            size="sm"
            class="page-limit"
          />
        </div>
        <div class="d-flex align-items-center">
          <BPagination
            v-model="paginaAtual"
            :per-page="limitePorPagina"
            :total-rows="paginacao.total"
            :disabled="loading"
            size="sm"
            limit="5"
            class="mb-0"
          />
        </div>
      </div>

    </template>
  </div>
</template>

<script setup>
import { BFormSelect, BPagination } from 'bootstrap-vue-next';
import {
  computed,
  onMounted,
  reactive,
  ref,
  watch
} from 'vue';

import cobrancasApi
  from '@/services/cobrancas.api';

import {
  usePermissions
} from '@/composables/usePermissions';


const limitePorPagina = ref(20);


const {
  fetchPermissions,
  hasPermission
} =
  usePermissions();


const loadingResumo =
  ref(false);

const loadingProblemas =
  ref(false);

const errorMessage =
  ref('');

const resumo =
  ref(null);

const problemas =
  ref([]);

const paginaAtual =
  ref(1);


const paginacao =
  reactive({
    pagina:
      1,

    limite:
      limitePorPagina.value,

    total:
      0,

    totalPaginas:
      0
  });


const pageOptions = [10, 15, 20, 25, 50].map(value => ({ value, text: `${value} / página` }));
const startRow = computed(() => paginacao.total === 0 ? 0 : (paginacao.pagina - 1) * paginacao.limite + 1);
const endRow = computed(() => Math.min(paginacao.pagina * paginacao.limite, paginacao.total));

const podeVisualizar =
  computed(() =>
    hasPermission(
      'financeiro_cobrancas_visualizar'
    )
  );


const loading =
  computed(() =>
    loadingResumo.value ||
    loadingProblemas.value
  );


const resumoCards =
  computed(() => {
    const operacao =
      resumo.value?.operacao ??
      {};

    return [
      {
        key:
          'pendentes',

        label:
          'Pendentes agora',

        value:
          operacao
            .pendentesDisponiveis ??
          '—'
      },

      {
        key:
          'backoff',

        label:
          'Aguardando nova tentativa',

        value:
          operacao
            .pendentesBackoff ??
          '—'
      },

      {
        key:
          'ativos',

        label:
          'Em processamento',

        value:
          operacao
            .processandoAtivos ??
          '—'
      },

      {
        key:
          'expirados',

        label:
          'Prazo de processamento expirado',

        value:
          operacao
            .processandoLeaseExpirado ??
          '—'
      },

      {
        key:
          'esgotados',

        label:
          'Encerrados com erro',

        value:
          operacao
            .esgotados ??
          '—'
      },

      {
        key:
          'maxTentativas',

        label:
          'Máx. de tentativas',

        value:
          operacao
            .maxTentativas ??
          '—'
      }
    ];
  });


function apiErrorMessage(
  error,
  fallback
) {
  const message =
    error?.response?.data?.message;

  if (
    typeof message ===
      'string' &&
    message.trim()
  ) {
    return message.trim();
  }

  return fallback;
}


function categoriaLabel(
  value
) {
  return {
    PENDENTE_DISPONIVEL:
      'Pendente para processamento',

    PROCESSANDO_LEASE_EXPIRADO:
      'Prazo de processamento expirado',

    ESGOTADO:
      'Encerrado com erro'
  }[value] || value || '—';
}


function categoriaBadgeClass(
  value
) {
  return {
    PENDENTE_DISPONIVEL:
      'text-bg-warning',

    PROCESSANDO_LEASE_EXPIRADO:
      'text-bg-danger',

    ESGOTADO:
      'text-bg-dark'
  }[value] || 'text-bg-secondary';
}


function tipoLabel(
  value
) {

  if (
    value ===
      'APLICAR_LIQUIDACAO'
  ) {
    return 'Aplicar liquidação';
  }

  return {
    WEBHOOK_PROCESSAR:
      'Retorno bancário',

    RECUPERAR_REGISTRO:
      'Recuperação de registro',

    RECUPERAR_CANCELAMENTO:
      'Recuperação de cancelamento'
  }[value] || value || '—';
}


function referenciaLabel(
  referencia
) {
  if (
    !referencia ||
    typeof referencia !==
      'object'
  ) {
    return '—';
  }

  if (
    referencia.tipo ===
      'WEBHOOK'
  ) {
    return `Retorno #${referencia.id}`;
  }

  if (
    referencia.tipo ===
      'COBRANCA'
  ) {
    return `Cobrança #${referencia.id}`;
  }

  return '—';
}


function formatTimestamp(
  value
) {
  if (!value) {
    return '—';
  }

  const date =
    new Date(
      value
    );

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return '—';
  }

  return new Intl.DateTimeFormat(
    'pt-BR',
    {
      dateStyle:
        'short',

      timeStyle:
        'short'
    }
  ).format(
    date
  );
}


function aplicarProblemas(
  result
) {
  problemas.value =
    Array.isArray(
      result?.itens
    )
      ? result.itens
      : [];

  const source =
    result?.paginacao ??
    {};

  paginacao.pagina =
    Number.isSafeInteger(
      source.pagina
    )
      ? source.pagina
      : paginaAtual.value;

  paginacao.limite =
    Number.isSafeInteger(
      source.limite
    )
      ? source.limite
      : limitePorPagina.value;

  paginacao.total =
    Number.isSafeInteger(
      source.total
    )
      ? source.total
      : 0;

  paginacao.totalPaginas =
    Number.isSafeInteger(
      source.totalPaginas
    )
      ? source.totalPaginas
      : 0;
}


async function carregarResumo() {
  loadingResumo.value =
    true;

  try {
    resumo.value =
      await cobrancasApi
        .obterResumoJobs();

    return true;
  } catch (error) {
    resumo.value =
      null;

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar o resumo operacional.'
      );

    return false;
  } finally {
    loadingResumo.value =
      false;
  }
}


async function carregarProblemas() {
  loadingProblemas.value =
    true;

  try {
    const result =
      await cobrancasApi
        .listarProblemasJobs({
          pagina:
            paginaAtual.value,

          limite:
            limitePorPagina.value
        });

    aplicarProblemas(
      result
    );

    const ultimaPagina = Math.max(paginacao.totalPaginas, 1);
    if (paginaAtual.value > ultimaPagina) {
      paginaAtual.value = ultimaPagina;
    }

    return true;
  } catch (error) {
    problemas.value =
      [];

    Object.assign(
      paginacao,
      {
        pagina:
          paginaAtual.value,

        limite:
          limitePorPagina.value,

        total:
          0,

        totalPaginas:
          0
      }
    );

    errorMessage.value =
      apiErrorMessage(
        error,
        'Não foi possível carregar os problemas operacionais.'
      );

    return false;
  } finally {
    loadingProblemas.value =
      false;
  }
}


async function carregarTudo() {
  if (
    !podeVisualizar.value ||
    loading.value
  ) {
    return;
  }

  errorMessage.value =
    '';

  await Promise.all([
    carregarResumo(),
    carregarProblemas()
  ]);
}


watch([paginaAtual, limitePorPagina], async ([pagina, limite], [, limiteAnterior]) => {
  if (limite !== limiteAnterior && pagina !== 1) {
    paginaAtual.value = 1;
    return;
  }
  errorMessage.value = '';
  await carregarProblemas();
});


onMounted(
  async () => {
    try {
      await fetchPermissions();
    } catch {
      errorMessage.value =
        'Não foi possível validar sua permissão de acesso.';

      return;
    }

    if (
      !podeVisualizar.value
    ) {
      errorMessage.value =
        'Você não possui permissão para visualizar cobranças.';

      return;
    }

    await carregarTudo();
  }
);
</script>

<style scoped>
.page-limit { width: 6rem; }
:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}

.monitoramento-cobrancas-jobs {
  min-width: 0;
}

.monitoramento-resumo-grid {
  display: grid;
  grid-template-columns:
    repeat(
      3,
      minmax(0, 1fr)
    );
  gap: 0.75rem;
}

.monitoramento-resumo-grid .card {
  min-width: 0;
}

table th,
table td {
  vertical-align: middle;
}

code {
  white-space: nowrap;
}

@media (max-width: 991.98px) {
  .monitoramento-resumo-grid {
    grid-template-columns:
      repeat(
        2,
        minmax(0, 1fr)
      );
  }
}

@media (max-width: 575.98px) {
  .monitoramento-resumo-grid {
    grid-template-columns:
      1fr;
  }
}
</style>
